import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  MessageCircle,
  Send,
  Loader2,
  AtSign,
  User,
  Building2,
  Wrench,
  Package,
  UserCog,
  X,
  ChevronDown,
  Pencil,
  Trash2,
  Check,
  MoreVertical
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function TicketChat({ ticketId, ticketType, token, currentUser }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState("");
  const [selectedMentions, setSelectedMentions] = useState([]);
  const [isExpanded, setIsExpanded] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);
  const [editText, setEditText] = useState("");
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const wsRef = useRef(null);
  const chatContainerRef = useRef(null);

  // WebSocket connection for real-time updates
  useEffect(() => {
    if (!ticketId || !token) return;

    // Construct WebSocket URL
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = process.env.REACT_APP_BACKEND_URL?.replace(/^https?:\/\//, '') || window.location.host;
    const wsUrl = `${wsProtocol}//${wsHost}/ws/chat/${ticketType}_${ticketId}?token=${token}`;

    const connectWebSocket = () => {
      try {
        wsRef.current = new WebSocket(wsUrl);

        wsRef.current.onopen = () => {
          console.log('WebSocket connected for chat');
          setWsConnected(true);
        };

        wsRef.current.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === 'new_message') {
              setMessages(prev => {
                // Avoid duplicates
                if (prev.some(m => m.id === data.message.id)) return prev;
                return [...prev, data.message];
              });
            }
          } catch (err) {
            console.error('Failed to parse WebSocket message:', err);
          }
        };

        wsRef.current.onclose = () => {
          console.log('WebSocket disconnected');
          setWsConnected(false);
          // Attempt reconnection after 3 seconds
          setTimeout(() => {
            if (ticketId && token) connectWebSocket();
          }, 3000);
        };

        wsRef.current.onerror = (error) => {
          console.error('WebSocket error:', error);
        };
      } catch (err) {
        console.error('Failed to connect WebSocket:', err);
      }
    };

    connectWebSocket();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [ticketId, ticketType, token]);

  useEffect(() => {
    if (ticketId && token) {
      fetchMessages();
      fetchParticipants();
    }
  }, [ticketId, token]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchMessages = async () => {
    try {
      const res = await fetch(
        `${API}/tickets/${ticketType}/${ticketId}/messages`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
      }
    } catch (err) {
      console.error("Failed to fetch messages:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchParticipants = async () => {
    try {
      const res = await fetch(
        `${API}/tickets/${ticketType}/${ticketId}/participants`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setParticipants(data.participants || []);
      }
    } catch (err) {
      console.error("Failed to fetch participants:", err);
    }
  };

  const handleSend = async () => {
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      const res = await fetch(
        `${API}/tickets/${ticketType}/${ticketId}/messages`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            message: newMessage,
            mentions: selectedMentions.map(m => m.id || m.email)
          })
        }
      );

      if (!res.ok) throw new Error("Failed to send message");

      const data = await res.json();
      setMessages(prev => [...prev, data.message]);
      setNewMessage("");
      setSelectedMentions([]);
      
      if (data.mentions_notified > 0) {
        toast.success(`Message sent, ${data.mentions_notified} user(s) notified`);
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
    if (e.key === "@") {
      setShowMentions(true);
    }
    if (e.key === "Escape") {
      setShowMentions(false);
    }
  };

  const handleMentionSelect = (participant) => {
    if (!selectedMentions.find(m => m.email === participant.email)) {
      setSelectedMentions(prev => [...prev, participant]);
      // Insert @mention into message
      const mention = `@${participant.name} `;
      setNewMessage(prev => prev + mention);
    }
    setShowMentions(false);
    setMentionSearch("");
    textareaRef.current?.focus();
  };

  // Edit message
  const startEditing = (msg) => {
    setEditingMessage(msg.id);
    setEditText(msg.message);
  };

  const cancelEditing = () => {
    setEditingMessage(null);
    setEditText("");
  };

  const saveEdit = async (msgId) => {
    if (!editText.trim()) return;
    
    try {
      const res = await fetch(
        `${API}/${ticketType}/messages/${msgId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ message: editText })
        }
      );

      if (!res.ok) throw new Error("Failed to update message");

      setMessages(prev => prev.map(m => 
        m.id === msgId ? { ...m, message: editText, edited_at: new Date().toISOString() } : m
      ));
      setEditingMessage(null);
      setEditText("");
      toast.success("Message updated");
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Delete message
  const deleteMessage = async (msgId) => {
    if (!confirm("Are you sure you want to delete this message?")) return;
    
    try {
      const res = await fetch(
        `${API}/${ticketType}/messages/${msgId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (!res.ok) throw new Error("Failed to delete message");

      setMessages(prev => prev.filter(m => m.id !== msgId));
      toast.success("Message deleted");
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Handle @ key in textarea
  const handleInputChange = (e) => {
    const value = e.target.value;
    setNewMessage(value);
    
    // Check if @ was typed at the end
    if (value.endsWith("@")) {
      setShowMentions(true);
      setMentionSearch("");
    }
    
    // Track mention search if dropdown is open
    if (showMentions) {
      const lastAtIndex = value.lastIndexOf("@");
      if (lastAtIndex !== -1) {
        const searchText = value.substring(lastAtIndex + 1);
        if (!searchText.includes(" ")) {
          setMentionSearch(searchText);
        } else {
          setShowMentions(false);
        }
      }
    }
  };

  const removeMention = (email) => {
    setSelectedMentions(prev => prev.filter(m => m.email !== email));
  };

  const filteredParticipants = participants.filter(p =>
    p.name.toLowerCase().includes(mentionSearch.toLowerCase()) ||
    p.email.toLowerCase().includes(mentionSearch.toLowerCase())
  );

  const getTypeIcon = (type) => {
    switch (type) {
      case "hospital": return <Building2 className="w-3 h-3" />;
      case "contractor": return <Wrench className="w-3 h-3" />;
      case "supplier": return <Package className="w-3 h-3" />;
      case "admin": return <UserCog className="w-3 h-3" />;
      default: return <User className="w-3 h-3" />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case "hospital": return "bg-blue-100 text-blue-700";
      case "contractor": return "bg-amber-100 text-amber-700";
      case "supplier": return "bg-purple-100 text-purple-700";
      case "admin": return "bg-emerald-100 text-emerald-700";
      case "system": return "bg-slate-100 text-slate-600";
      default: return "bg-slate-100 text-slate-600";
    }
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return "Just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    
    return date.toLocaleDateString();
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden relative" data-testid="ticket-chat" ref={chatContainerRef}>
      {/* Header */}
      <div 
        className="bg-slate-50 px-4 py-3 border-b border-slate-200 flex items-center justify-between cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          <MessageCircle className="w-5 h-5 text-purple-600" />
          <span className="font-semibold text-slate-800">Conversation</span>
          <span className="text-xs text-slate-500 bg-slate-200 px-2 py-0.5 rounded-full">
            {messages.length} messages
          </span>
          {/* Real-time connection status */}
          <span 
            className={`w-2 h-2 rounded-full ${wsConnected ? 'bg-green-500' : 'bg-slate-300'}`}
            title={wsConnected ? 'Real-time connected' : 'Connecting...'}
          />
        </div>
        <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${isExpanded ? "" : "-rotate-90"}`} />
      </div>

      {isExpanded && (
        <>
          {/* Messages */}
          <div className="h-96 overflow-y-auto p-4 space-y-4 bg-slate-50/50">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-slate-400">
                <MessageCircle className="w-12 h-12 mb-2 opacity-50" />
                <p className="text-sm">No messages yet</p>
                <p className="text-xs">Start the conversation below</p>
              </div>
            ) : (
              messages.map((msg) => {
                const isOwnMessage = msg.sender_email === currentUser?.email;
                const isEditing = editingMessage === msg.id;
                
                return (
                  <div
                    key={msg.id}
                    className={`flex ${isOwnMessage ? "justify-end" : "justify-start"} group`}
                  >
                    <div
                      className={`max-w-[85%] rounded-xl px-4 py-2 relative ${
                        msg.is_system
                          ? "bg-slate-100 text-slate-600 text-center text-sm italic w-full max-w-full"
                          : isOwnMessage
                          ? "bg-purple-600 text-white"
                          : "bg-white border border-slate-200 text-slate-800"
                      }`}
                    >
                      {/* Message Header */}
                      {!msg.is_system && (
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs px-1.5 py-0.5 rounded flex items-center gap-1 ${
                            isOwnMessage 
                              ? "bg-purple-500 text-purple-100" 
                              : getTypeColor(msg.sender_type)
                          }`}>
                            {getTypeIcon(msg.sender_type)}
                            {msg.sender_name}
                          </span>
                          <span className={`text-xs ${
                            isOwnMessage ? "text-purple-200" : "text-slate-400"
                          }`}>
                            {formatTime(msg.created_at)}
                            {msg.edited_at && " (edited)"}
                          </span>
                          
                          {/* Edit/Delete dropdown for own messages */}
                          {isOwnMessage && !msg.is_system && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button className="opacity-0 group-hover:opacity-100 transition-opacity ml-auto">
                                  <MoreVertical className="w-4 h-4 text-purple-200 hover:text-white" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-32">
                                <DropdownMenuItem onClick={() => startEditing(msg)}>
                                  <Pencil className="w-4 h-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => deleteMessage(msg.id)}
                                  className="text-red-600"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      )}
                      
                      {/* Message Content - Editable or Display */}
                      {isEditing ? (
                        <div className="space-y-2">
                          <Textarea
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            className="min-h-[60px] text-slate-800 bg-white"
                            autoFocus
                          />
                          <div className="flex gap-2 justify-end">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={cancelEditing}
                              className="text-purple-200 hover:text-white hover:bg-purple-500"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => saveEdit(msg.id)}
                              className="bg-purple-500 hover:bg-purple-400"
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm whitespace-pre-wrap break-words">{msg.message}</p>
                      )}
                      
                      {/* Mentions display */}
                      {msg.mentions?.length > 0 && !isEditing && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {msg.mentions.map((m, i) => (
                            <span key={i} className={`text-xs px-1.5 py-0.5 rounded ${
                              isOwnMessage ? "bg-purple-500 text-purple-100" : "bg-purple-100 text-purple-700"
                            }`}>
                              @{m.name}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Selected Mentions */}
          {selectedMentions.length > 0 && (
            <div className="px-4 py-2 bg-purple-50 border-t border-purple-100 flex flex-wrap gap-2">
              <span className="text-xs text-purple-600 flex items-center gap-1">
                <AtSign className="w-3 h-3" /> Notifying:
              </span>
              {selectedMentions.map((m) => (
                <span
                  key={m.email}
                  className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full flex items-center gap-1"
                >
                  {m.name}
                  <button onClick={() => removeMention(m.email)} className="hover:text-purple-900">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="p-4 border-t border-slate-200 bg-white relative">
            {/* Mention Dropdown - positioned above input */}
            {showMentions && (
              <div className="absolute bottom-full left-4 right-4 mb-2 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto z-50">
                <div className="p-2 border-b border-slate-100 sticky top-0 bg-white">
                  <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                    <AtSign className="w-4 h-4 text-purple-500" />
                    Tag someone in this message
                  </div>
                  <input
                    type="text"
                    placeholder="Search people..."
                    value={mentionSearch}
                    onChange={(e) => setMentionSearch(e.target.value)}
                    className="w-full text-sm px-2 py-1 border border-slate-200 rounded focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                    autoFocus
                  />
                </div>
                {filteredParticipants.length === 0 ? (
                  <p className="p-3 text-sm text-slate-500 text-center">No participants found</p>
                ) : (
                  <div className="max-h-32 overflow-y-auto">
                    {filteredParticipants.map((p) => (
                      <button
                        key={p.email}
                        onClick={() => handleMentionSelect(p)}
                        className="w-full px-3 py-2 text-left hover:bg-purple-50 flex items-center gap-2 border-b border-slate-50 last:border-0"
                      >
                        <span className={`p-1.5 rounded ${getTypeColor(p.type)}`}>
                          {getTypeIcon(p.type)}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">{p.name}</p>
                          <p className="text-xs text-slate-500 truncate">{p.email}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                <div className="p-2 border-t border-slate-100 bg-slate-50">
                  <p className="text-xs text-slate-400">Press Esc to close</p>
                </div>
              </div>
            )}
            
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Textarea
                  ref={textareaRef}
                  value={newMessage}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  placeholder="Type a message... (@ to tag someone)"
                  className="min-h-[80px] max-h-40 resize-none pr-10"
                  data-testid="chat-input"
                />
                <button
                  onClick={() => setShowMentions(!showMentions)}
                  className={`absolute right-3 bottom-3 transition-colors ${
                    showMentions ? "text-purple-600" : "text-slate-400 hover:text-purple-600"
                  }`}
                  title="Tag someone (@)"
                >
                  <AtSign className="w-5 h-5" />
                </button>
              </div>
              <Button
                onClick={handleSend}
                disabled={sending || !newMessage.trim()}
                className="bg-purple-600 hover:bg-purple-700 self-end"
                data-testid="send-message-btn"
              >
                {sending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
            <p className="text-xs text-slate-400 mt-2">
              Press Enter to send, Shift+Enter for new line, @ to tag someone
            </p>
          </div>
        </>
      )}
    </div>
  );
}
